import React, { useState, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { SyutGrid, type SyutGridColumn } from 'components/ui';
import { useSnackbar } from 'components/common/SnackbarNotification';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useGoldRates } from 'hooks/api';
import { GridLoadingSkeleton } from 'components/common';
// ============================================================================
// TYPES
// ============================================================================

interface GoldRateHistoryRecord {
  id: string;
  rateDate: string;
  ratePerGram: number;
  isActive: boolean;
  entityName: string;
  updatedByName: string;
  updatedAt: string;
}

// ============================================================================
// MAIN SCREEN
// ============================================================================

const GoldRateHistoryReportScreen: React.FC = () => {
  const { showMessage } = useSnackbar();

  // API hooks
  const { data: apiData, loading: apiLoading, error: apiError, refetch } = useGoldRates();
  // Data from API
  const data = (apiData as unknown as GoldRateHistoryRecord[]) || [];
  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeOnly, setActiveOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const searchable = `${row.rateDate} ${row.entityName} ${row.updatedByName}`.toLowerCase();
        if (!searchable.includes(q)) return false;
      }
      if (activeOnly && !row.isActive) return false;
      if (dateFrom && row.rateDate < dateFrom) return false;
      if (dateTo && row.rateDate > dateTo) return false;
      return true;
    });
  }, [data, searchQuery, activeOnly, dateFrom, dateTo]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<GoldRateHistoryRecord>[] = useMemo(() => [
    {
      id: 'rateDate',
      header: 'Rate Date',
      accessorKey: 'rateDate',
      size: 120,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
    {
      id: 'ratePerGram',
      header: 'Rate Per Gram (INR)',
      accessorKey: 'ratePerGram',
      size: 170,
      cell: ({ getValue }: any) => <span style={{ fontWeight: 600 }}>{formatCurrency(getValue())}</span>,
    },
    {
      id: 'isActive',
      header: 'Status',
      accessorKey: 'isActive',
      size: 110,
      cell: ({ getValue }: any) => {
        const active = getValue() as boolean;
        return <span style={getStatusBadgeStyle(active ? 'ACTIVE' : 'INACTIVE')}>{active ? 'Active' : 'Inactive'}</span>;
      },
    },
    { id: 'entityName', header: 'Entity', accessorKey: 'entityName', size: 150 },
    { id: 'updatedByName', header: 'Updated By', accessorKey: 'updatedByName', size: 140 },
    {
      id: 'updatedAt',
      header: 'Updated At',
      accessorKey: 'updatedAt',
      size: 120,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
  ], []);

  // ============================================================================
  // VIEW MODE & ACTION BUTTONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
  ];

  const actionButtons: ActionButton[] = [
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      variant: 'icon',
      tooltip: 'Refresh',
      onClick: () => refetch(),
    },
    {
      id: 'export',
      icon: <IconSystem.download size={16} />,
      label: 'Export',
      variant: 'primary',
      tooltip: 'Export Report',
      onClick: () => showMessage('Export functionality will be available after API integration.', 'info'),
    },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Gold Rate History"
        icon={<IconSystem.chart size={20} />}
        description="Historical gold rate trends with activation timeline."
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search rates...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode) }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
        actions={actionButtons}
      />

      <div className="module-content">
        {apiError && data.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load report data</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}
        {apiLoading && data.length === 0 ? (
          <GridLoadingSkeleton columns={6} />
        ) : (
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <SyutGrid
              data={filteredData}
              columns={tableColumns}
              enableRowSelection={false}
              enablePagination
              enableColumnFilters={false}
              emptyMessage="No gold rate records match the current filters."
            />
          </div>
        </div>
        )}

        {/* Filter Panel */}
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
              <IconSystem.x size={14} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Date From</label>
              <input
                type="date"
                className="form-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Date To</label>
              <input
                type="date"
                className="form-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                fontSize: '0.8rem', color: 'var(--text-primary)', cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={activeOnly}
                  onChange={(e) => setActiveOnly(e.target.checked)}
                  style={{ accentColor: 'var(--primary)' }}
                />
                Active Only
              </label>
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => { setActiveOnly(false); setDateFrom(''); setDateTo(''); }}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoldRateHistoryReportScreen;
